import React, { useState, useMemo } from 'react';
import _ from 'lodash';

import { IconButton } from '../common/IconButton';
import ModelContextEditor from './ModelContextEditor';

const groupKey = (ctx) => `${ctx.year ? 'withyear' : 'withoutyear'}-${JSON.stringify(_.toPairs(_.omit(ctx, 'year', 'years')).sort())}`;

const expandYears = (ctx) => {
  const { year, years, ...rest } = ctx;
  const all = _.uniq([...(years || []), ...(year != null ? [year] : [])]);
  if (all.length === 0) return [rest];
  if (all.length === 1) return [{ ...rest, year: all[0] }];
  return all.map(y => ({ ...rest, year: y }));
};

export default function MultipleContextsEditor({ value, onChange, locale }) {
  const [ungrouped, setUngrouped] = useState(() => new Set());

  const flat = useMemo(() => {
    let arr = _.isArray(value) ? value : [value || {}];
    return arr.length === 0 ? [{}] : arr;
  }, [value]);

  const rows = useMemo(() => {
    const groups = _.groupBy(flat, groupKey);
    const result = [];
    for (const [key, items] of Object.entries(groups)) {
      if (ungrouped.has(key) || items.length === 1 || items.length > _.uniqBy(items, 'year').length) {
        for (const item of items) {
          result.push({ ctx: item, items: [item], realItems: items, key, grouped: false });
        }
      } else {
        const base = _.omit(items[0], 'year', 'years');
        const years = _.uniq(items.flatMap(c => [...(c.years || []), ...(c.year != null ? [c.year] : [])]));
        const merged = years.length > 1 ? { ...base, years }
          : years.length === 1 ? { ...base, year: years[0] }
            : base;
        result.push({ ctx: merged, items, key, grouped: years.length > 1 });
      }
    }
    return result;
  }, [flat, ungrouped]);

  const replaceRow = (row, newItems) => {
    const next = [];
    let inserted = false;
    for (const item of flat) {
      if (row.items.includes(item)) {
        if (!inserted) {
          next.push(...newItems);
          inserted = true;
        }
      } else {
        next.push(item);
      }
    }
    onChange(next);
  };

  const updateRow = (row, ctx) => replaceRow(row, expandYears(ctx));
  const removeRow = (row) => replaceRow(row, []);
  const copyRow = (row) => onChange([...flat, ..._.cloneDeep(row.items)]);
  const addRow = () => onChange([...flat, {}]);

  const toggleUngroup = (key) => {
    setUngrouped(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return <div>
    {rows.map((row, i) => (
      <div key={i} className={'d-flex mb-1 align-items-center'}>
        <div className={'flex-grow-1'}>
          <ModelContextEditor value={row.ctx} onChange={(c) => updateRow(row, c)} locale={locale} />
        </div>
        <div>
          {i === rows.length - 1 ? (
            <IconButton onClick={addRow} level={'success'} icon={'add_circle'} description={'Add context'} />
          ) : null}
          
          {
            !row.grouped && ungrouped.has(row.key) && row.realItems?.length > 1 ? <>
              <IconButton onClick={() => toggleUngroup(row.key)} level={'warning'} icon={'merge'}
                description={'Group years'} />
            </> : null
          }

          {rows.length > 1 ? (
            <IconButton onClick={() => removeRow(row)} level={'danger'} icon={'remove_circle_outline'}
              description={'Remove context'} />
          ) : null}


          {row.grouped ? <>
            <span className={'badge badge-warning align-middle ml-1'}>{row.ctx.years.length}</span>
            <IconButton onClick={() => toggleUngroup(row.key)} level={'warning'} icon={'call_split'}
              description={'Ungroup years'} />
          </> :
            <IconButton onClick={() => copyRow(row)} level={'primary'} icon={'content_copy'} description={'Copy'} />
          }
        </div>
      </div>
    ))}
  </div>;
}
